/* eslint-disable react/require-default-props */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { translate } from '@utils/i18n';
import RichTextEditor from '@components/v2/RichTextEditor';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import { namespace } from '../constants';
import { useResources, useUpdateResource } from '../../hooks/useRichContentResources';
import ResourceSelector from './ResourceSelector';
import './RichContentEntryStyles.scss';
export default function RichContentEntryComponent({ entry, fieldType, onAddEntry, onUpdate, onDelete, isLoading = false, isSaving = false, isDeleting = false, error = null }) {
    const [entryToUpdate, setEntryToUpdate] = useState(entry);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [resourceSelectError, setResourceSelectError] = useState(null);
    // Fetch resources using the new hook
    const { data: allResources = [], isLoading: isLoadingResources, error: resourcesError } = useResources();
    const updateResourceMutation = useUpdateResource();
    useEffect(() => {
        if (entry.id && entry.id !== entryToUpdate.id) {
            setEntryToUpdate(entry);
        }
    }, [entry.id, entryToUpdate.id, entry]);
    // Filter resources by fieldType
    const filteredResources = useMemo(() => {
        const typeMap = {
            summary: 'Summary',
            scopeOfWork: 'ScopeOfWork',
            exclusions: 'Exclusion',
            termsAndConditions: 'TermsAndCondition'
        };
        return allResources.filter((resource) => resource.type === typeMap[fieldType]);
    }, [allResources, fieldType]);
    const handleSave = useCallback((newEntry) => {
        if (newEntry.body === entry.body && newEntry.resourceId === entry.resourceId) {
            return;
        }
        try {
            setSaveError(null);
            if (newEntry.id) {
                onUpdate(Object.assign(Object.assign({}, newEntry), { id: newEntry.id }));
            }
            else {
                onAddEntry(newEntry);
            }
        }
        catch (err) {
            setSaveError(err instanceof Error ? err.message : 'Failed to save changes');
        }
    }, [onUpdate, onAddEntry, entry.body, entry.resourceId]);
    const handleResourceSelect = useCallback((resourceId) => {
        const selectedResource = filteredResources.find((r) => r.id === resourceId);
        if (selectedResource) {
            try {
                const newEntry = Object.assign(Object.assign({}, entryToUpdate), { body: selectedResource.body, resourceId });
                setResourceSelectError(null);
                setEntryToUpdate(newEntry);
                if (newEntry.id) {
                    handleSave(Object.assign(Object.assign({}, newEntry), { id: newEntry.id }));
                }
                else {
                    onAddEntry(newEntry);
                }
            }
            catch (err) {
                setResourceSelectError(err instanceof Error ? err.message : 'Failed to select resource');
            }
        }
    }, [filteredResources, entryToUpdate, onAddEntry, handleSave]);
    const handleDelete = useCallback(() => {
        try {
            onDelete(entryToUpdate.id);
            setShowDeleteConfirm(false);
        }
        catch (err) {
            // Error handling is done at parent level
            setShowDeleteConfirm(false);
        }
    }, [entryToUpdate.id, onDelete]);
    const handleUpdateResource = useCallback(() => {
        if (entryToUpdate.resourceId && entryToUpdate.body) {
            const typeMap = {
                summary: 'Summary',
                scopeOfWork: 'ScopeOfWork',
                exclusions: 'Exclusion',
                termsAndConditions: 'TermsAndCondition'
            };
            updateResourceMutation.mutate({
                id: entryToUpdate.resourceId,
                type: typeMap[fieldType],
                body: entryToUpdate.body
            });
        }
    }, [entryToUpdate.resourceId, entryToUpdate.body, fieldType, updateResourceMutation]);
    const hasUnsavedChanges = entryToUpdate.body !== entry.body;
    const isEmpty = !entryToUpdate.body || entryToUpdate.body.trim().length === 0;
    const hasError = error || saveError || resourceSelectError || resourcesError;
    const isDisabled = isLoading || isSaving || isDeleting || isLoadingResources || updateResourceMutation.isLoading;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: `rich-content-entry ${isEmpty ? 'empty' : ''} ${hasError ? 'has-error' : ''} ${isDisabled ? 'disabled' : ''}`, role: "region", "aria-labelledby": `entry-title-${(entry === null || entry === void 0 ? void 0 : entry.id) || ''}`, "aria-describedby": hasError ? `entry-error-${(entry === null || entry === void 0 ? void 0 : entry.id) || ''}` : undefined },
            React.createElement(ResourceSelector, { resources: filteredResources, entryId: entry.id, selectedResourceId: entryToUpdate.resourceId, onResourceSelect: handleResourceSelect, onUpdateResource: handleUpdateResource, onDeleteEntry: () => setShowDeleteConfirm(true), placeholder: translate('content_sections.select_template', {
                    namespace,
                    type: fieldType
                }), disabled: isDisabled || isLoadingResources, isLoading: isLoading || isLoadingResources || updateResourceMutation.isLoading, error: resourceSelectError || (resourcesError === null || resourcesError === void 0 ? void 0 : resourcesError.message) })),
        React.createElement("div", { className: "content-editor-container", role: "group", "aria-labelledby": `editor-label-${(entry === null || entry === void 0 ? void 0 : entry.id) || ''}` },
            React.createElement(RichTextEditor, { value: entryToUpdate.body, onChange: (content) => {
                    setEntryToUpdate(Object.assign(Object.assign({}, entryToUpdate), { body: content }));
                }, onBlur: () => handleSave(entryToUpdate), placeholder: translate('placeholders.enter_content', {
                    namespace,
                    title: fieldType
                }), disabled: isDisabled, "aria-labelledby": `editor-label-${(entry === null || entry === void 0 ? void 0 : entry.id) || ''}`, "aria-describedby": hasUnsavedChanges ? `unsaved-indicator-${(entry === null || entry === void 0 ? void 0 : entry.id) || ''}` : undefined })),
        showDeleteConfirm && (React.createElement(ConfirmDialog, { title: translate('content_sections.confirm_delete_title', { namespace }), description: translate('content_sections.confirm_delete_message', { namespace }), onConfirm: handleDelete, onCancel: () => setShowDeleteConfirm(false), yesLabel: translate('delete', { namespace: 'features.commons' }), noLabel: translate('cancel', { namespace: 'features.commons' }) }))));
}
